import { displayTimezone, formatDate, formatDateRange } from '@unimpaired/utils'
import { twMerge } from 'tailwind-merge'
import {
  getBgColorClasses,
  getBorderColorClasses,
  getTimeColorClasses,
  getTitleColorClasses,
  toDayScheduleEventColor,
} from './schedule.colors'
import {
  getColumnEventIsIn,
  getGridRow,
  totalDayScheduleColumns,
} from './schedule.positioning.helpers'
import { DayScheduleProps, ScheduleEvent } from './DaySchedule.types'
import { RenderHTMLContent } from '../Text'

export type DayScheduleEventProps = {
  startDayAt: Date
  endDayAt: Date
  colStart?: number
  colWidth?: number
  className?: string
  layoutId: string
  onClick: () => any
  minDisplayDurationMinutes?: number
  condensed?: boolean
  occluded?: boolean
  timezone?: string
  hideTimezone?: boolean
  minEventWidth: number
} & ScheduleEvent

export const DayScheduleEvent = ({
  startDayAt,
  endDayAt,
  colStart,
  colWidth,
  className,
  onClick,
  layoutId: _layoutId,
  eventDescriptor: _eventDescriptor,
  minDisplayDurationMinutes = 15,
  timezone,
  hideTimezone,
  minEventWidth,
  ...event
}: DayScheduleEventProps) => {
  // 12 grid rows per hour (5-min buckets), 60 columns total
  const gridRow = getGridRow({
    eventStartTime: event.startTime,
    eventEndTime: event.endTime,
    startDayAt,
    endDayAt,
    minDisplayDurationMinutes,
  })
  const gridColumn = `${colStart || 1} / span ${colWidth ?? totalDayScheduleColumns}`

  const eventColor = toDayScheduleEventColor(event.color)
  const resolvedTimezone = timezone || displayTimezone(event)
  return (
    <li
      className={twMerge(
        'relative mt-px flex overflow-visible',
        event.muted && 'opacity-50',
        className,
      )}
      style={{ gridRow, gridColumn, minWidth: minEventWidth + 'px' }}>
      <button
        type='button'
        onClick={(e) => {
          e.stopPropagation()
          if (onClick) onClick()
        }}
        className={twMerge(
          `group transition-all mb-1 mx-1 gap-x-4 border-1 border-l-4 shadow overflow-hidden flex flex-col min-w-0 flex-1 w-0 justify-start items-start content-start grow rounded-lg px-2 hover:px-2.5 hover:pb-0.5 text-xs leading-5`,
          !onClick && 'cursor-pointer',
          getBgColorClasses(eventColor),
          getBorderColorClasses(eventColor),
        )}>
        <div
          className={twMerge(
            'flex flex-wrap gap-x-4 justify-start items-start content-start max-w-full',
            event.condensed && 'flex-nowrap',
          )}>
          <span
            className={twMerge(
              'whitespace-nowrap min-w-fit overflow-hidden',
              getTimeColorClasses(eventColor),
            )}>
            {event.condensed
              ? // condensed cell is too tight for a zone suffix
                formatDate('time', event.startTime, resolvedTimezone, true)
              : formatDateRange(
                  ['time'],
                  [event.startTime, event.endTime],
                  resolvedTimezone,
                  hideTimezone,
                )}
          </span>
          <span
            className={twMerge(
              'font-semibold min-w-min shrink overflow-hidden text-ellipsis text-left',
              event.condensed && 'flex-1 min-w-0 shrink grow text-nowrap',
              getTitleColorClasses(eventColor),
            )}>
            {event.name}
          </span>
        </div>
        {event.headline && (
          <RenderHTMLContent
            className={twMerge(
              'overflow-hidden text-wrap pt-0.5 text-xs overflow-ellipsis text-left min-w-min max-w-full flex-1 self-stretch pb-1 text-base-1000',
            )}>
            {event.headline}
          </RenderHTMLContent>
        )}
      </button>
    </li>
  )
}

export const getDayScheduleLayoutId = <T extends ScheduleEvent>(
  columnateBy: DayScheduleProps<T>['columnateBy'],
  event: T,
  ...otherKeys: (string | number)[]
) =>
  event.name +
  (getColumnEventIsIn(columnateBy)(event)?.key || '') +
  otherKeys.join('-')
